/**
 * Card order and promotion rules for BLU TRV Main (heat vs % open).
 * Presentation only — RPCs stay in the view. Next-schedule lives in NextScheduleCard.
 */

const celsiusToFahrenheit = (celsius: number): number => Number(((celsius * 9) / 5 + 32).toFixed(1));

export const TRV_LOW_BATTERY_PERCENT = 20;
export const TRV_TARGET_REACHED_DELTA_C = 0.15;

export type TrvControlMode = 'temperature' | 'position';

export type TrvOverlay = 'offline' | 'uncalibrated';

export type TrvPriorityCardPriority = 'primary' | 'wide' | 'secondary' | 'tertiary';

export type TrvPriorityCardTone = 'warning' | 'danger' | 'success' | 'muted' | 'info';

export type TrvPriorityLabel = {
	key: string;
	default: string;
};

export type TrvPriorityCard = {
	id: 'valve' | 'currentTemperature' | 'battery';
	value: string;
	unit?: string;
	numericValue?: number;
	label: TrvPriorityLabel;
	icon: string;
	priority: TrvPriorityCardPriority;
	tone?: TrvPriorityCardTone;
	stale?: boolean;
	kind?: 'metric' | 'status';
	accentColor?: string;
};

export type TrvPriorityLayout = {
	mode: TrvControlMode;
	overlay: TrvOverlay | null;
	statusLabel: TrvPriorityLabel;
	showNextSchedule: boolean;
	cards: TrvPriorityCard[];
};

export type TrvPriorityLayoutInput = {
	enableTrv: boolean;
	online: boolean;
	currentC?: number | null;
	targetC?: number | null;
	position?: number | null;
	battery?: number | null;
	boost?: boolean;
	temperatureUnit?: string;
	calibrated?: boolean;
};

export const TRV_OVERLAY_COPY: Record<
	TrvOverlay,
	{
		value: TrvPriorityLabel;
		label: TrvPriorityLabel;
		icon: string;
		tone: TrvPriorityCardTone;
		cta?: TrvPriorityLabel;
	}
> = {
	offline: {
		value: { key: 'label_sensor_overlay_offline', default: 'Offline' },
		label: {
			key: 'label_sensor_overlay_offline_hint',
			default: 'Device unavailable',
		},
		icon: 'fa-solid fa-cloud-slash',
		tone: 'muted',
	},
	uncalibrated: {
		value: { key: 'label_not_calibrated', default: 'Not calibrated' },
		label: {
			key: 'label_trv_overlay_uncalibrated_hint',
			default: 'Calibration needed',
		},
		icon: 'fa-solid fa-triangle-exclamation',
		tone: 'warning',
		cta: { key: 'button_calibrate', default: 'Calibrate' },
	},
};

const formatTemp = (celsius: number, unit: string): string => {
	if (unit === 'F') {
		return String(celsiusToFahrenheit(celsius));
	}
	return String(Number(celsius.toFixed(1)));
};

const isTargetReached = (currentC: number | null | undefined, targetC: number | null | undefined) =>
	currentC != null && targetC != null && Math.abs(currentC - targetC) <= TRV_TARGET_REACHED_DELTA_C;

const positionStatusLabel = (position: number | null | undefined): TrvPriorityLabel => {
	if (position == null) {
		return { key: 'label_trv_status_position', default: 'Position' };
	}
	if (position <= 0) {
		return { key: 'label_trv_status_closed', default: 'Closed' };
	}
	if (position >= 100) {
		return { key: 'label_trv_status_open', default: 'Open' };
	}
	return { key: 'label_trv_status_partial', default: 'Partially open' };
};

export const trvPriorityFlipKey = (layout: TrvPriorityLayout): string =>
	[
		layout.mode,
		layout.overlay ?? '',
		...layout.cards.map((card) => `${card.id}:${card.priority}`),
	].join('|');

export const buildTrvPriorityLayout = (input: TrvPriorityLayoutInput): TrvPriorityLayout => {
	const unit = input.temperatureUnit === 'F' ? 'F' : 'C';
	const mode: TrvControlMode = input.enableTrv ? 'temperature' : 'position';
	const overlay: TrvOverlay | null = !input.online
		? 'offline'
		: input.calibrated === false
			? 'uncalibrated'
			: null;
	const stale = overlay === 'offline';
	const battery = input.battery;
	const lowBattery = battery != null && battery <= TRV_LOW_BATTERY_PERCENT;
	const boost = Boolean(input.boost);
	const showNextSchedule = !boost && !lowBattery && !overlay;

	const statusLabel: TrvPriorityLabel =
		overlay === 'offline'
			? { key: 'label_sensor_overlay_offline', default: 'Offline' }
			: mode === 'position'
				? positionStatusLabel(input.position)
				: boost
					? { key: 'sub_settings_title_boost', default: 'Boost' }
					: isTargetReached(input.currentC, input.targetC)
						? { key: 'label_trv_status_target_reached', default: 'Target reached' }
						: { key: 'label_trv_status_heating', default: 'Heating' };

	const cards: TrvPriorityCard[] = [];

	if (mode === 'temperature') {
		if (input.position != null) {
			cards.push({
				id: 'valve',
				value: String(Math.round(input.position)),
				unit: '%',
				numericValue: input.position,
				label: { key: 'label_valve_position', default: 'Valve position' },
				icon: input.position > 0 ? 'fas fa-pipe-valve' : 'far fa-pipe-valve',
				priority: boost
					? 'primary'
					: isTargetReached(input.currentC, input.targetC)
						? 'tertiary'
						: 'secondary',
				tone: boost ? 'warning' : undefined,
				stale,
				accentColor: 'var(--color-theme-climate-warm)',
			});
		}
	} else if (input.currentC != null) {
		cards.push({
			id: 'currentTemperature',
			value: formatTemp(input.currentC, unit),
			unit: `°${unit}`,
			numericValue: input.currentC,
			label: { key: 'label_current_temperature', default: 'Current temperature' },
			icon: 'fa-solid fa-temperature-half',
			priority: lowBattery ? 'tertiary' : 'secondary',
			stale,
			accentColor: 'var(--color-theme-climate-warm)',
		});
	}

	if (battery != null) {
		cards.push({
			id: 'battery',
			value: String(Math.round(battery)),
			unit: '%',
			numericValue: battery,
			label: lowBattery
				? { key: 'label_trv_replace_battery', default: 'Replace battery soon' }
				: { key: 'label_battery', default: 'Battery' },
			icon:
				battery <= 5
					? 'fas fa-battery-empty'
					: lowBattery
						? 'fas fa-battery-low'
						: 'fa-solid fa-battery-three-quarters',
			priority: lowBattery
				? 'primary'
				: isTargetReached(input.currentC, input.targetC) && mode === 'temperature'
					? 'tertiary'
					: 'secondary',
			tone: lowBattery ? 'warning' : undefined,
			stale,
		});
	}

	return { mode, overlay, statusLabel, showNextSchedule, cards };
};
